import {FormEvent} from 'react';
import {Link} from 'react-router-dom';
import Icon from '../Icon';
import ErrorBox, {FieldError, isGeneralError} from '../feedback/ErrorBox';
import Sheet from '../feedback/Sheet';
import {usePlaceMutations} from '../../api/places';
import {useFormDraft} from '../../hooks/useFormDraft';
import {withReturn} from '../../hooks/useReturnTo';
import {groupPlaces, PLACE_TYPE_ICONS, PLACE_TYPE_LABELS, SPOT_TYPES} from '../../lib/placeTree';
import {Place, PlaceType} from '../../types';
import Button from '../Button';

// "+ New" on the Where chips: make the place right here and select it, no trip to the Places
// screen. Pickers add in place; screens are for tidying. The half-typed name survives a reload
// through the form draft (quietly, not in the banner).
type Props = {
    open: boolean;
    onClose: () => void;
    places: Place[];
    /** The area to preselect, usually the one of the currently chosen chip. */
    suggestedAreaId: string | null;
    onCreated: (place: Place) => void;
    /** The form's own route, so "Manage places" can offer a way back. */
    returnTo: string;
};

type Values = {name: string; type: PlaceType; parentId: string | null; asArea: boolean};

export default function AddPlaceSheet({open, onClose, places, suggestedAreaId, onCreated, returnTo}: Props) {
    const {create} = usePlaceMutations();
    const areas = groupPlaces(places).map(g => g.area);
    const form = useFormDraft<Values>('place:new', {name: '', type: 'bed', parentId: suggestedAreaId ?? areas[0]?.id ?? null, asArea: false}, {label: 'A new place', route: returnTo, listed: false});
    const v = form.values;
    // A restored draft can name an area that has since been hidden or removed.
    const parentId = areas.some(a => a.id === v.parentId) ? v.parentId : (suggestedAreaId ?? areas[0]?.id ?? null);

    const submit = (event: FormEvent) => {
        event.preventDefault();
        create.mutate(v.asArea ? {name: v.name.trim(), type: 'area', parentId: null} : {name: v.name.trim(), type: v.type, parentId}, {
            onSuccess: place => {
                form.clear();
                form.discard();
                onCreated(place);
                onClose();
            }
        });
    };

    return (
        <Sheet open={open} onClose={onClose} title={v.asArea ? 'New area' : 'New place'} busy={create.isPending}>
            <form onSubmit={submit} className="flex flex-col gap-5">
                <label className="flex flex-col gap-1">
                    <span className="meta">Name</span>
                    <input className="field" type="text" maxLength={40} autoFocus required placeholder={v.asArea ? 'Front garden, Allotment…' : 'Bed 2, Big pot by the door…'} value={v.name} onChange={e => form.set({name: e.target.value})} />
                    <FieldError error={create.error} field="name" />
                </label>

                {!v.asArea && (
                    <>
                        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                            <legend className="meta mb-1 p-0">In which area?</legend>
                            <div className="flex flex-wrap gap-2">
                                {areas.map(a => (
                                    <button key={a.id} type="button" className="chip" aria-pressed={parentId === a.id} onClick={() => form.set({parentId: a.id})}>
                                        {a.name}
                                    </button>
                                ))}
                            </div>
                            <FieldError error={create.error} field="parentId" />
                        </fieldset>
                        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                            <legend className="meta mb-1 p-0">What is it?</legend>
                            <div className="flex flex-wrap gap-2">
                                {SPOT_TYPES.map(t => (
                                    <button key={t} type="button" className="chip" aria-pressed={v.type === t} onClick={() => form.set({type: t})}>
                                        <Icon name={PLACE_TYPE_ICONS[t]} size={18} />
                                        {PLACE_TYPE_LABELS[t]}
                                    </button>
                                ))}
                            </div>
                        </fieldset>
                    </>
                )}

                {isGeneralError(create.error) && <ErrorBox error={create.error} />}

                <Button type="submit" busy={create.isPending} busyLabel="Adding…" disabled={v.name.trim() === '' || (!v.asArea && parentId === null)}>
                    <Icon name="plus" size={20} />
                    {v.asArea ? 'Add the area' : 'Add and use it'}
                </Button>

                <div className="flex items-center justify-between">
                    <button type="button" className="meta underline-offset-2 hover:underline" onClick={() => form.set({asArea: !v.asArea})}>
                        {v.asArea ? 'Add a bed or spot instead' : 'Add a whole new area instead'}
                    </button>
                    <Link to={withReturn('/places', returnTo)} className="meta no-underline underline-offset-2 hover:underline">
                        Manage places
                    </Link>
                </div>
            </form>
        </Sheet>
    );
}
